<section class="sender-identities-page">
  <!-- Top Bar: Standard 76px edge-to-edge full-width header -->
  <header class="page-topbar hero-section">
    <div class="topbar-title-block title-block">
      <h1 class="page-title">Sender Identities</h1>
      <p class="page-subtitle helper-copy">
        Manage verified email addresses and custom brands to send agreements under your organization.
      </p>
    </div>

    <button type="button" class="add-btn btn-purple" (click)="openAddModal()">
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
      <span>Add Sender Identity</span>
    </button>
  </header>

  <div class="sender-identities-body">
    <section class="manage-section" aria-labelledby="sender-identities-list-title">
      <!-- Search & Stats Toolbar exactly matching Documents -->
      <div class="search-bar-row">
        <div class="search-field-wrap search-box">
          <input
            type="text"
            [(ngModel)]="searchQuery"
            placeholder="Search Users"
          />

          @if (searchQuery) {
            <button
              type="button"
              class="search-clear-btn"
              (click)="clearSearch()"
              [disabled]="loading"
              aria-label="Clear search"
              title="Clear search"
            >
              ×
            </button>
          }

          <span class="search-icon-box" aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="9" cy="9" r="5.5"></circle>
              <line x1="13.5" y1="13.5" x2="17.5" y2="17.5"></line>
            </svg>
          </span>
        </div>

        <div class="total-identities-count">
          <strong>{{ filteredSenderIdentities.length }}</strong>&nbsp;<span>{{ identitiesCountLabel() }}</span>
        </div>
      </div>

      @if (feedbackMessage) {
        <div class="alert-banner success">
          <p class="status-note" aria-live="polite">{{ feedbackMessage }}</p>
        </div>
      }

      @if (errorMessage) {
        <div class="alert-banner error">
          <p class="status-note status-note-error" aria-live="polite">{{ errorMessage }}</p>
        </div>
      }

      <!-- Reusable c-table with global 10px rounded thead and floating card rows -->
      <div class="table-container table-card">
        <div class="table-scroll">
          <table class="c-table">
            <thead>
              <tr>
                <th class="name-col">Name</th>
                <th class="email-col">Email Address</th>
                <th class="status-col">Status</th>
                <th class="created-col">Created On</th>
                <th class="approved-col">Approved Date</th>
                <th class="brand-col">Brand</th>
                <th class="lang-col">Language</th>
                <th class="actions-col"><span class="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              @if (loading) {
                <tr>
                  <td colspan="8" class="c-table-loading-cell empty-row">
                    <div class="c-loading-state">
                      <div class="c-spinner"></div>
                      <p class="c-loading-text">Loading sender identities…</p>
                    </div>
                  </td>
                </tr>
              } @else if (filteredSenderIdentities.length === 0) {
                <tr>
                  <td colspan="8" class="empty-row">
                    <div class="empty-state-wrap">
                      <div class="empty-state-icon">
                        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.8">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="8.5" cy="7" r="4"></circle>
                          <line x1="20" y1="8" x2="20" y2="14"></line>
                          <line x1="23" y1="11" x2="17" y2="11"></line>
                        </svg>
                      </div>
                      <p class="empty-state-text">No sender identities found.</p>
                      <p class="empty-state-sub">Add a sender identity with a verified email to send agreements under your name.</p>
                      <button type="button" class="add-btn btn-purple" (click)="openAddModal()">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        <span>Add Sender Identity</span>
                      </button>
                    </div>
                  </td>
                </tr>
              } @else {
                @for (identity of filteredSenderIdentities; track trackBySenderIdentity($index, identity)) {
                  <tr class="sender-row" [class.sender-row-pending]="identity.status === 'pending'">
                    <td class="name-cell">
                      <span class="sender-name">{{ identity.name }}</span>
                    </td>
                    <td class="email-cell">
                      <a class="email-link" [href]="'mailto:' + identity.email">{{ identity.email }}</a>
                    </td>
                    <td class="status-cell">
                      <span
                        class="status-pill"
                        [class.status-pill-verified]="identity.status === 'verified'"
                        [class.status-pill-pending]="identity.status === 'pending'"
                        [class.status-pill-denied]="identity.status === 'denied'"
                      >
                        {{ identity.status === 'verified' ? 'Verified' : identity.status === 'denied' ? 'Denied' : 'Pending' }}
                      </span>
                    </td>
                    <td class="date-cell">{{ formatDateTime(identity.createdAt) }}</td>
                    <td class="date-cell">{{ formatDateTime(identity.approvedAt) }}</td>
                    <td class="brand-cell">
                      <span class="brand-badge">{{ identity.brand }}</span>
                    </td>
                    <td class="lang-cell">{{ identity.language }}</td>
                    <td class="actions-cell">
                      <div class="menu-shell" data-sender-identity-menu [class.is-open]="isMenuOpen(identity.id)">
                        <button
                          type="button"
                          class="row-action"
                          cdkOverlayOrigin
                          #menuOrigin="cdkOverlayOrigin"
                          aria-haspopup="menu"
                          [attr.aria-expanded]="isMenuOpen(identity.id)"
                          [class.is-active]="isMenuOpen(identity.id)"
                          aria-label="Sender identity actions"
                          (click)="toggleMenu($event, identity.id)"
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                            <circle cx="12" cy="5" r="2"></circle>
                            <circle cx="12" cy="12" r="2"></circle>
                            <circle cx="12" cy="19" r="2"></circle>
                          </svg>
                        </button>

                        <ng-template
                          cdkConnectedOverlay
                          [cdkConnectedOverlayOrigin]="menuOrigin"
                          [cdkConnectedOverlayOpen]="isMenuOpen(identity.id)"
                          [cdkConnectedOverlayPositions]="menuPositions"
                          [cdkConnectedOverlayPush]="true"
                          [cdkConnectedOverlayViewportMargin]="12"
                          (overlayOutsideClick)="closeMenu()"
                        >
                          <div class="actions-menu" data-sender-identity-menu role="menu" [attr.aria-label]="identity.name + ' actions'">
                            @if (identity.status === 'verified') {
                              <button type="button" class="menu-item" role="menuitem" (click)="openNotificationModal(identity)">
                                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2">
                                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                                </svg>
                                <span>Email Notification</span>
                              </button>
                            }
                            <button type="button" class="menu-item" role="menuitem" (click)="openEditModal(identity)">
                              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                              <span>Edit</span>
                            </button>
                            @if (identity.status === 'pending') {
                              <button
                                type="button"
                                class="menu-item"
                                role="menuitem"
                                [disabled]="isResending(identity.id)"
                                (click)="resendVerification(identity)"
                              >
                                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2">
                                  <polyline points="23 4 23 10 17 10"></polyline>
                                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                                </svg>
                                <span>{{ isResending(identity.id) ? 'Resending…' : 'Resend Verification' }}</span>
                              </button>
                            }
                            <button type="button" class="menu-item is-destructive" role="menuitem" (click)="openDeleteConfirm(identity)">
                              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              </svg>
                              <span>Delete</span>
                            </button>
                          </div>
                        </ng-template>
                      </div>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      </div>
    </section>
  </div>

  <!-- Add / Edit Modal -->
  @if (modalOpen) {
    <section class="sender-modal-mask modal-mask modal-mask--open" role="presentation" (click)="closeModal()">
      <div
        class="sender-modal modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sender-identity-modal-title"
        (click)="$event.stopPropagation()"
      >
        <header class="sender-modal-header modal-header">
          <h2 id="sender-identity-modal-title">
            {{ modalMode === 'edit' ? 'Edit Sender Identity' : 'Add Sender Identity' }}
          </h2>
          <button type="button" class="sender-modal-close modal-close-btn" (click)="closeModal()" aria-label="Close sender identity modal">
            ×
          </button>
        </header>

        <div class="sender-modal-body modal-body">
          <label class="modal-field form-field">
            <span class="form-label">Name{{ modalMode === 'add' ? ' *' : '' }}</span>
            <input type="text" [(ngModel)]="form.name" placeholder="Enter name" />
          </label>

          <label class="modal-field form-field">
            <span class="form-label">Email Address *</span>
            <input
              type="email"
              [(ngModel)]="form.email"
              placeholder="Enter email address"
              [readonly]="modalMode === 'edit'"
              [disabled]="modalMode === 'edit'"
            />
          </label>

          <label class="modal-field form-field">
            <span class="form-label">{{ modalMode === 'edit' ? 'Brand' : 'Select Brand' }}</span>
            <select [(ngModel)]="form.brandId" [disabled]="brandsLoading">
              <option value="">None</option>
              @for (brand of brands; track brand.brandId) {
                <option [value]="brand.brandId">{{ brand.name }}</option>
              }
            </select>
          </label>

          @if (modalErrorMessage) {
            <p class="modal-error inline-note inline-note-error" aria-live="polite">{{ modalErrorMessage }}</p>
          }
        </div>

        <footer class="sender-modal-actions modal-footer">
          <button type="button" class="ghost-btn" (click)="closeModal()" [disabled]="saving">Cancel</button>
          <button
            type="button"
            class="primary-btn btn-purple"
            (click)="modalMode === 'edit' ? submitEditSenderIdentity() : submitAddSenderIdentity()"
            [disabled]="modalMode === 'edit' ? !canUpdate : !canSubmit"
          >
            @if (modalMode === 'edit') {
              {{ saving ? 'Updating…' : 'Update' }}
            } @else {
              {{ saving ? 'Adding…' : 'Add' }}
            }
          </button>
        </footer>
      </div>
    </section>
  }

  <!-- Email Notification Modal -->
  @if (notificationModalOpen && notificationTarget; as notificationIdentity) {
    <section class="sender-modal-mask modal-mask modal-mask--open" role="presentation" (click)="closeNotificationModal()">
      <div
        class="notification-modal modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sender-identity-notification-title"
        (click)="$event.stopPropagation()"
      >
        <header class="sender-modal-header notification-modal-header modal-header">
          <div class="notification-modal-title">
            <h2 id="sender-identity-notification-title">Email Notification</h2>
            <label class="notification-switch" aria-label="Enable email notifications">
              <input
                type="checkbox"
                [checked]="notificationSettings.enabled"
                [disabled]="notificationLoading || notificationSaving"
                (change)="setNotificationEnabled($any($event.target).checked)"
              />
              <span class="notification-switch-track" aria-hidden="true"></span>
            </label>
          </div>
          <button type="button" class="sender-modal-close modal-close-btn" (click)="closeNotificationModal()" aria-label="Close email notification modal">
            ×
          </button>
        </header>

        <div class="notification-modal-body modal-body">
          @if (notificationLoading) {
            <p class="notification-state">Loading email notification settings…</p>
          } @else {
            <p class="notification-recipient">{{ notificationIdentity.email }}</p>

            <label class="notification-item notification-item-select-all">
              <input
                type="checkbox"
                [checked]="allNotificationsSelected"
                [indeterminate]="someNotificationsSelected && !allNotificationsSelected"
                [disabled]="notificationInputsDisabled"
                (change)="toggleAllNotifications($any($event.target).checked)"
              />
              <span>Select All</span>
            </label>

            <div class="notification-list">
              @for (option of notificationOptions; track option.key) {
                <label class="notification-item">
                  <input
                    type="checkbox"
                    [checked]="notificationSettings.notifications[option.key]"
                    [disabled]="notificationInputsDisabled"
                    (change)="updateNotificationPreference(option.key, $any($event.target).checked)"
                  />
                  <span>{{ option.label }}</span>
                </label>
              }
            </div>
          }

          @if (notificationErrorMessage) {
            <p class="modal-error inline-note inline-note-error" aria-live="polite">{{ notificationErrorMessage }}</p>
          }
        </div>

        <footer class="sender-modal-actions modal-footer">
          <button
            type="button"
            class="ghost-btn"
            (click)="closeNotificationModal()"
            [disabled]="notificationLoading || notificationSaving"
          >
            Cancel
          </button>
          <button
            type="button"
            class="primary-btn btn-purple"
            (click)="submitNotificationSettings()"
            [disabled]="!canSaveNotificationSettings"
          >
            {{ notificationSaving ? 'Saving…' : 'Save' }}
          </button>
        </footer>
      </div>
    </section>
  }

  <!-- Delete Confirm Modal -->
  @if (deleteTarget; as identityToDelete) {
    <section class="delete-modal-mask modal-mask modal-mask--open" role="presentation" (click)="closeDeleteConfirm()">
      <div
        class="delete-modal modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-sender-identity-title"
        (click)="$event.stopPropagation()"
      >
        <div class="delete-modal-body modal-body">
          <div class="delete-icon-wrap">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </div>
          <h3 id="delete-sender-identity-title">Delete sender identity?</h3>
          <p class="delete-copy">
            Are you sure you want to permanently delete this sender identity? This action cannot be undone.
          </p>
          <p class="delete-modal-detail">
            <strong>{{ identityToDelete.name }}</strong> · {{ identityToDelete.email }}
          </p>
        </div>
        <footer class="delete-modal-actions modal-footer">
          <button type="button" class="ghost-btn" (click)="closeDeleteConfirm()" [disabled]="deleting">Cancel</button>
          <button type="button" class="danger-btn" (click)="confirmDelete()" [disabled]="deleting">
            {{ deleting ? 'Deleting…' : 'Yes, Delete' }}
          </button>
        </footer>
      </div>
    </section>
  }
</section>
